import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Alert, App, Button, Card, Descriptions, Input, Modal, Segmented, Space, Spin, Table, Typography } from 'antd';
import { ArrowLeftOutlined, PlayCircleOutlined } from '@ant-design/icons';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import recognitionService, { type RunItem } from '@/services/financeRecognition.service';
import { StatusTag, amount, apiErrorMessage, asObject, contractPath, humanize, jvPath, useRecognitionAccess } from './prepaidDisplay';

const { Title, Text } = Typography;

const PrepaidRunDetailPage: React.FC = () => {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const access = useRecognitionAccess();
  const [status, setStatus] = useState<string>('ALL');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [reason, setReason] = useState('');

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['recognition', 'run', id, status, page, pageSize],
    queryFn: () => recognitionService.getRun(id!, { status: status === 'ALL' ? undefined : status, page, limit: pageSize }),
    placeholderData: keepPreviousData,
    enabled: Boolean(id),
    refetchInterval: (q) => (q.state.data?.run.status === 'PROCESSING' ? 3000 : false),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['recognition'] });
  const processMutation = useMutation({
    mutationFn: () => recognitionService.processRun(id!),
    onSuccess: (r) => {
      message.success(`Run ${humanize(r.status).toLowerCase()}`);
      invalidate();
    },
    onError: (e) => message.error(apiErrorMessage(e, 'Processing failed')),
  });
  const discardMutation = useMutation({
    mutationFn: () => recognitionService.discardRun(id!, reason.trim()),
    onSuccess: () => {
      message.success(t('runDiscarded'));
      setDiscardOpen(false);
      invalidate();
    },
    onError: (e) => message.error(apiErrorMessage(e, 'Discard failed')),
  });

  if (isLoading && !data) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }
  if (isError || !data) return <Alert type="error" showIcon style={{ margin: 24 }} message={apiErrorMessage(error, 'Run not found')} />;

  const { run, items, status_counts: counts } = data;
  const processable = ['VALIDATED', 'PARTIALLY_COMPLETED'].includes(run.status);
  const discardable = ['DRAFT', 'VALIDATED'].includes(run.status);
  const filters = asObject(run.filters);

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ marginBottom: 16 }} wrap>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/fin/prepaid/runs')}>
          {t('runs')}
        </Button>
        <Title level={4} style={{ margin: 0 }}>
          {run.run_number}
        </Title>
        <StatusTag status={run.status} />
        {processable && access.canRun && access.canPost && (
          <Button type="primary" icon={<PlayCircleOutlined />} loading={processMutation.isPending} onClick={() => processMutation.mutate()}>
            {run.status === 'VALIDATED' ? 'Process' : 'Retry exceptions'}
          </Button>
        )}
        {discardable && access.canRun && (
          <Button danger onClick={() => setDiscardOpen(true)}>
            {t('discard')}
          </Button>
        )}
      </Space>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Descriptions size="small" column={{ xs: 1, md: 3 }}>
          <Descriptions.Item label={t('throughDate')}>{run.recognition_through_date}</Descriptions.Item>
          <Descriptions.Item label={t('actionCol')}>{run.posting_action === 'DRAFT' ? 'Create draft journals' : 'Post journals'}</Descriptions.Item>
          <Descriptions.Item label={t('granularity')}>{humanize(run.posting_granularity)}</Descriptions.Item>
          <Descriptions.Item label={t('lines')}>{run.item_count}</Descriptions.Item>
          <Descriptions.Item label={t('eligibleBase')}>{amount(run.eligible_amount)}</Descriptions.Item>
          <Descriptions.Item label={t('postedBase')}>{amount(run.posted_amount)}</Descriptions.Item>
          <Descriptions.Item label={t('exceptions')}>
            {run.exception_count} ({amount(run.exception_amount)})
          </Descriptions.Item>
          <Descriptions.Item label={t('triggeredBy')}>{run.triggered_by === 'USER' ? run.created_by_name || 'User' : humanize(run.triggered_by)}</Descriptions.Item>
          <Descriptions.Item label={t('startedCompleted')}>
            {run.started_at ? dayjs(run.started_at).format('YYYY-MM-DD HH:mm') : '—'} / {run.completed_at ? dayjs(run.completed_at).format('YYYY-MM-DD HH:mm') : '—'}
          </Descriptions.Item>
          {Object.keys(filters).length > 0 && (
            <Descriptions.Item label={t('filters')} span={3}>
              <Text type="secondary">
                {Object.entries(filters)
                  .filter(([, v]) => v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0))
                  .map(([k, v]) => `${humanize(k)}: ${Array.isArray(v) ? `${v.length} selected` : String(v)}`)
                  .join(' · ') || 'None'}
              </Text>
            </Descriptions.Item>
          )}
        </Descriptions>
        {run.failure_message && <Alert type="error" showIcon style={{ marginTop: 8 }} message={run.failure_message} />}
      </Card>

      <Card size="small">
        <Segmented
          style={{ marginBottom: 12 }}
          value={status}
          onChange={(v) => {
            setStatus(String(v));
            setPage(1);
          }}
          options={[{ label: `All (${Object.values(counts).reduce((s, n) => s + n, 0)})`, value: 'ALL' }, ...Object.entries(counts).map(([k, n]) => ({ label: `${humanize(k)} (${n})`, value: k }))]}
        />
        <Table<RunItem>
          size="small"
          rowKey="id"
          dataSource={items.data}
          loading={isLoading}
          pagination={{
            current: page,
            pageSize,
            total: items.total,
            showSizeChanger: true,
            onChange: (p, s) => {
              setPage(p);
              setPageSize(s);
            },
          }}
          columns={[
            { title: t('contract'), dataIndex: 'recognition_number', render: (v: string, r) => <Link to={contractPath(r.recognition_contract_id)}>{v}</Link> },
            { title: t('description'), dataIndex: 'description', ellipsis: true },
            { title: t('period'), dataIndex: 'accounting_period_key', width: 90 },
            { title: t('postingDate'), dataIndex: 'posting_date', width: 110, render: (v: string) => String(v ?? '').slice(0, 10) },
            { title: t('amountBase'), dataIndex: 'base_amount', width: 140, align: 'right', render: (v: string) => amount(v) },
            {
              title: t('result'),
              dataIndex: 'status',
              width: 260,
              render: (s: string, r) => (
                <Space direction="vertical" size={0}>
                  <StatusTag status={s} />
                  {r.exception_message && (
                    <Text type="danger" style={{ fontSize: 12 }}>
                      {r.exception_message}
                    </Text>
                  )}
                </Space>
              ),
            },
            { title: t('lineStatus'), dataIndex: 'line_status', width: 140, render: (s: string) => <StatusTag status={s} /> },
            { title: t('jv'), key: 'jv', width: 150, render: (_: unknown, r) => (r.journal_voucher_id ? <Link to={jvPath(r.journal_voucher_id)}>{r.journal_voucher_number}</Link> : '—') },
          ]}
        />
      </Card>

      <Modal
        open={discardOpen}
        title={`Discard ${run.run_number}`}
        okText={t('discard')}
        okButtonProps={{ danger: true, disabled: reason.trim().length < 3 }}
        confirmLoading={discardMutation.isPending}
        onCancel={() => setDiscardOpen(false)}
        onOk={() => discardMutation.mutate()}
      >
        <Text type="secondary">{t('discardRunLinesHint')}</Text>
        <Input.TextArea rows={3} style={{ marginTop: 12 }} placeholder={t('reasonRequired')} value={reason} onChange={(e) => setReason(e.target.value)} />
      </Modal>
    </div>
  );
};

export default PrepaidRunDetailPage;
